import { FACEMESH_TRIANGULATION } from './faceMeshTriangulation'

export const getFaceMeshTriangles = (maxPoints = 468) => {
  if (!Number.isFinite(maxPoints) || maxPoints >= 468) return FACEMESH_TRIANGULATION
  const filtered = []
  for (let i = 0; i < FACEMESH_TRIANGULATION.length; i += 3) {
    const a = FACEMESH_TRIANGULATION[i]
    const b = FACEMESH_TRIANGULATION[i + 1]
    const c = FACEMESH_TRIANGULATION[i + 2]
    if (a < maxPoints && b < maxPoints && c < maxPoints) {
      filtered.push(a, b, c)
    }
  }
  return filtered
}

const applyAffineTransform = (ctx, s0, s1, s2, d0, d1, d2) => {
  const sx0 = s0.x
  const sy0 = s0.y
  const sx1 = s1.x
  const sy1 = s1.y
  const sx2 = s2.x
  const sy2 = s2.y

  const dx0 = d0.x
  const dy0 = d0.y
  const dx1 = d1.x
  const dy1 = d1.y
  const dx2 = d2.x
  const dy2 = d2.y

  const det = sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1)
  if (det === 0) return false

  const a = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) / det
  const b = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) / det
  const c = (dx0 * (sx2 - sx1) + dx1 * (sx0 - sx2) + dx2 * (sx1 - sx0)) / det
  const d = (dy0 * (sx2 - sx1) + dy1 * (sx0 - sx2) + dy2 * (sx1 - sx0)) / det
  const e =
    (dx0 * (sx1 * sy2 - sx2 * sy1) +
      dx1 * (sx2 * sy0 - sx0 * sy2) +
      dx2 * (sx0 * sy1 - sx1 * sy0)) /
    det
  const f =
    (dy0 * (sx1 * sy2 - sx2 * sy1) +
      dy1 * (sx2 * sy0 - sx0 * sy2) +
      dy2 * (sx0 * sy1 - sx1 * sy0)) /
    det

  ctx.setTransform(a, b, c, d, e, f)
  return true
}

export const warpTriangles = (ctx, image, srcPoints, dstPoints, triangles, options = {}) => {
  if (!ctx || !image || !srcPoints || !dstPoints || !triangles) return
  const count = Math.min(srcPoints.length, dstPoints.length)
  if (!count) return

  ctx.save()
  ctx.imageSmoothingEnabled = true
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high'
  ctx.setTransform(1, 0, 0, 1, 0, 0)

  const depths = options.depths
  let drawOrder = null

  // If a depth buffer is provided, sort triangles by average Z-depth
  // Painter's Algorithm: Furthest triangles drawn first (behind, then on top)
  if (depths && depths.length >= count) {
    const numTriangles = Math.floor(triangles.length / 3)
    drawOrder = new Array(numTriangles)
    for (let i = 0; i < numTriangles; i++) {
      const idx = i * 3
      const ia = triangles[idx]
      const ib = triangles[idx + 1]
      const ic = triangles[idx + 2]

      let zAvg = 0
      if (ia < count && ib < count && ic < count) {
        zAvg = (depths[ia] + depths[ib] + depths[ic]) / 3
      }
      drawOrder[i] = { idx, zAvg }
    }
    // Sort descending so the smallest (most negative / furthest back Z) draws first.
    // NOTE: Lower Z implies further backwards. We draw the furthest first (Painter's).
    drawOrder.sort((a, b) => a.zAvg - b.zAvg)
  }

  const numLoops = drawOrder ? drawOrder.length : Math.floor(triangles.length / 3)

  for (let i = 0; i < numLoops; i += 1) {
    const baseIdx = drawOrder ? drawOrder[i].idx : i * 3
    const ia = triangles[baseIdx]
    const ib = triangles[baseIdx + 1]
    const ic = triangles[baseIdx + 2]

    if (ia >= count || ib >= count || ic >= count) continue

    const s0 = srcPoints[ia]
    const s1 = srcPoints[ib]
    const s2 = srcPoints[ic]
    // Expand destination triangle slightly to overlap neighbors and hide seams
    const od0 = dstPoints[ia]
    const od1 = dstPoints[ib]
    const od2 = dstPoints[ic]
    // Validate using original destination points before expansion
    if (!s0 || !s1 || !s2 || !od0 || !od1 || !od2) continue

    const cx = (od0.x + od1.x + od2.x) / 3
    const cy = (od0.y + od1.y + od2.y) / 3
    const EPS = 0.6
    const expand = (v) => {
      const vx = v.x - cx
      const vy = v.y - cy
      const len = Math.hypot(vx, vy) || 1
      return { x: v.x + (vx / len) * EPS, y: v.y + (vy / len) * EPS }
    }
    const d0 = expand(od0)
    const d1 = expand(od1)
    const d2 = expand(od2)

    ctx.save()
    ctx.beginPath()
    ctx.moveTo(d0.x, d0.y)
    ctx.lineTo(d1.x, d1.y)
    ctx.lineTo(d2.x, d2.y)
    ctx.closePath()
    ctx.clip()

    if (applyAffineTransform(ctx, s0, s1, s2, d0, d1, d2)) {
      ctx.drawImage(image, 0, 0)
    }
    ctx.restore()
  }

  ctx.restore()
}
